import { chakra } from '@chakra-ui/react';
import { Fragment, type ElementType } from 'react';
import { Icon, Icons } from '../icons';
import { focusRing } from '../primitives/_shared';
import type { LinkComponent } from './types';

export interface Crumb {
  label: string;
  /** When present the crumb is a link; the final crumb is normally plain (current). */
  to?: string;
}

const Nav = chakra('nav', { base: { minWidth: 0 } });
const OList = chakra('ol', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '1',
    listStyle: 'none',
    m: 0,
    p: 0,
    minWidth: 0,
  },
});
const LItem = chakra('li', { base: { display: 'flex', alignItems: 'center', gap: '1', minWidth: 0 } });

const CrumbLink = chakra('a', {
  base: {
    fontFamily: 'body',
    fontSize: 'sm',
    color: 'text.secondary',
    textDecoration: 'none',
    borderRadius: 'xs',
    whiteSpace: 'nowrap',
    _hover: { color: 'text.link', textDecoration: 'underline', textUnderlineOffset: '2px' },
    ...focusRing,
  },
});

const Current = chakra('span', {
  base: {
    fontFamily: 'body',
    fontSize: 'sm',
    fontWeight: 'medium',
    color: 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export interface BreadcrumbsProps {
  items: Crumb[];
  /** Router link component for crumbs that have a `to`. */
  linkComponent?: LinkComponent;
}

/** A trail of ancestor sections. The last crumb is the current page (`aria-current`). */
export function Breadcrumbs({ items, linkComponent }: BreadcrumbsProps) {
  if (items.length === 0) return null;
  const Link = (linkComponent ?? 'a') as ElementType;
  return (
    <Nav aria-label="Breadcrumb">
      <OList>
        {items.map((crumb, i) => {
          const isLast = i === items.length - 1;
          return (
            <Fragment key={`${crumb.label}-${i}`}>
              <LItem>
                {crumb.to && !isLast ? (
                  <CrumbLink
                    as={Link}
                    {...(linkComponent ? { to: crumb.to } : { href: crumb.to })}
                  >
                    {crumb.label}
                  </CrumbLink>
                ) : (
                  <Current aria-current={isLast ? 'page' : undefined}>{crumb.label}</Current>
                )}
              </LItem>
              {!isLast && (
                <chakra.li aria-hidden display="flex" alignItems="center">
                  <Icon as={Icons.ChevronRight} size="sm" tone="muted" />
                </chakra.li>
              )}
            </Fragment>
          );
        })}
      </OList>
    </Nav>
  );
}
